/**
 * 18 - Fuel Depot Raid (10 §2): 4-6 storage tanks in a wired depot, three in five of them is success. They
 * stand in clusters: within a cluster the tanks are close enough that one cooking off takes its neighbours
 * (one well-placed charge does for two or three), but the clusters are too far apart for the fire to jump -
 * a chain is possible, never automatic. Each tank's blast is large: the ranger must be clear of it.
 */

import { PROPS } from '../../sim/props';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, structure } from './helpers';
import { put } from './helpers3a';
import { checkTargets, cooksOff } from './helpers3aAsserts';
import type { ArchetypeSpec } from './types';

export const tanksFor = (threat: number): number => (threat <= 3 ? 4 : threat <= 6 ? 5 : 6);

/** Tank counts per cluster, west to east. */
const clustersFor = (tanks: number): number[] => (tanks === 4 ? [2, 2] : tanks === 5 ? [3, 2] : [2, 2, 2]);

/** Centre to centre within a cluster (north-south): 3.75 m from one tank's heart to the next hull - it cooks off. */
const WITHIN = 5;
/** Between clusters (east-west): 13.2 m between hulls - out of a cook-off's reach, and of one bomb's at both. */
const BETWEEN = 18;

export const fuelDepot: ArchetypeSpec = {
  id: 'fuel-depot',
  name: 'FUEL DEPOT RAID',
  nativeBiome: 'desert',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 64], d: [34, 34] },
  sizeFor: (threat) => ({ w: clustersFor(tanksFor(threat)).length * BETWEEN + 10, d: 34 }),
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const clusters = clustersFor(tanksFor(g.threat));
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    // North of the middle: the road comes in through the south gate and needs the room.
    const cz = (g.rect.z0 + g.rect.z1) / 2 - 3;
    const ids: number[] = [];
    clusters.forEach((count, c) => {
      const x = cx + (c - (clusters.length - 1) / 2) * BETWEEN;
      for (let i = 0; i < count; i++) {
        ids.push(put(g, 'fuelTank', x, cz + (i - (count - 1) / 2) * WITHIN, { target: true, margin: 0.5 }).id);
      }
      // Drums stacked at the foot of each cluster: they go up with it, and a grenade on them starts it.
      const foot = cz + ((count - 1) / 2) * WITHIN + 3.5;
      clutter(g, 'barrel', 2, { x, z: foot }, 2.5);
      guard(g, x + 4.5, foot, 0);
    });
    const office = structure(g, 'hut', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (office) guardAtDoor(g, office);
    guardsAbout(g, 1 + g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'BURN THE FUEL', targetPropIds: ids, minFraction: 0.6,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('fuel-depot');
    const tanks = checkTargets(check, def, ['fuelTank']);
    const wanted = tanksFor(def.threat);
    check(tanks.length === wanted, `needs ${wanted} tanks at threat ${def.threat}, has ${tanks.length}`);
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 0.6,
      'three tanks in five is success',
    );
    check((PROPS.fuelTank.secondaryBlast ?? 0) >= 8, 'a tank must go up with a large blast');
    check(tanks.every((t) => insideRect(def.goalZone, t.x, t.z)), 'the tanks stand inside the wire');
    // Chains: tanks are joined when one cooking off takes the other. Possible (some cluster of two or more),
    // never automatic (no cluster is the whole depot).
    const cluster = new Map<number, number>(tanks.map((t) => [t.id, t.id]));
    const root = (id: number): number => {
      let at = id;
      while (cluster.get(at) !== at) at = cluster.get(at) ?? at;
      return at;
    };
    for (const a of tanks) {
      for (const b of tanks) if (a.id < b.id && cooksOff(a, b)) cluster.set(root(b.id), root(a.id));
    }
    const sizes = new Map<number, number>();
    for (const t of tanks) sizes.set(root(t.id), (sizes.get(root(t.id)) ?? 0) + 1);
    check([...sizes.values()].some((n) => n >= 2), 'no two tanks are close enough for a chain cook-off');
    check(sizes.size >= 2, 'the whole depot is one chain: a single charge would burn it all');
    return out;
  },
};
